/**
 * All activity tab: flat audit + SIEM list (full scope).
 */

import { ChevronRight } from 'lucide-react';

import type { DropTimelineItem } from '@/hooks';
import { cn } from '@/lib/utils';

import { formatDropTimelineAt } from './drop-timeline-format';

function rowHint(source: DropTimelineItem['source']): string {
  return source === 'siem' ? 'Open SIEM log' : 'Open audit record';
}

export function TimelineRawPanel(props: {
  items: DropTimelineItem[];
  siemLimit: number;
  windowLabel: string;
  onSelectItem?: (item: DropTimelineItem) => void;
}) {
  const interactive = props.onSelectItem != null;

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        {props.windowLabel} · Up to {props.siemLimit} SIEM rows may appear (broad match on this drop).
      </p>
      {props.items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">No events in range.</p>
      ) : (
        <ul className="divide-y rounded-md border max-h-[55vh] overflow-y-auto">
          {props.items.map((item) => {
            const inner = (
              <>
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className="font-medium text-muted-foreground">
                    {formatDropTimelineAt(item.occurredAt)}
                  </span>
                  <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase">
                    {item.source}
                  </span>
                  {item.success === false ? (
                    <span className="text-xs text-destructive">failed</span>
                  ) : null}
                </div>
                <p className="mt-1 break-words">{item.summary}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {item.userEmail ?? '—'}
                  {item.action ? (
                    <>
                      {' '}
                      · <span className="font-mono">{item.action}</span>
                    </>
                  ) : null}
                </p>
                {interactive ? (
                  <p className="mt-2 flex items-center gap-1 text-xs font-medium text-primary">
                    {rowHint(item.source)}
                    <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  </p>
                ) : null}
              </>
            );

            return (
              <li
                key={`${item.source}-${item.id}`}
                className={cn(interactive && 'transition-colors hover:bg-muted/40')}
              >
                {interactive ? (
                  <button
                    type="button"
                    className="w-full px-3 py-2.5 text-left text-sm outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={() => props.onSelectItem?.(item)}
                  >
                    {inner}
                  </button>
                ) : (
                  <div className="px-3 py-2.5 text-sm">{inner}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
